import { useState, useEffect } from 'react';
import { schoolsApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { School, Clock, Brain, ChevronDown, ChevronUp, Save, Plus, Pencil, Trash2, Loader2, BellOff, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

const SENSITIVITY_PRESETS = {
  high: {
    label: 'Hög känslighet',
    description: 'Larmar vid lägre nivåer — bäst för högrisksmiljöer',
    high_threshold: 0.60,
    medium_threshold: 0.30,
    color: 'text-red-600',
    bg: 'bg-red-50 border-red-200',
  },
  medium: {
    label: 'Medel känslighet',
    description: 'Balanserad — standardinställning för de flesta skolor',
    high_threshold: 0.75,
    medium_threshold: 0.45,
    color: 'text-orange-600',
    bg: 'bg-orange-50 border-orange-200',
  },
  low: {
    label: 'Låg känslighet',
    description: 'Striktare trösklar — minskar falska positiva',
    high_threshold: 0.85,
    medium_threshold: 0.60,
    color: 'text-slate-600',
    bg: 'bg-slate-50 border-slate-200',
  },
  custom: {
    label: 'Anpassad',
    description: 'Manuellt konfigurerade trösklar',
    high_threshold: null,
    medium_threshold: null,
    color: 'text-violet-600',
    bg: 'bg-violet-50 border-violet-200',
  },
};

function MLConfigPanel({ school, onSave }) {
  const [expanded, setExpanded] = useState(false);
  const [sensitivity, setSensitivity] = useState(school.ml_sensitivity || 'medium');
  const [highThreshold, setHighThreshold] = useState(school.ml_high_threshold ?? 0.75);
  const [mediumThreshold, setMediumThreshold] = useState(school.ml_medium_threshold ?? 0.45);
  const [saving, setSaving] = useState(false);

  const handlePresetChange = (preset) => {
    setSensitivity(preset);
    if (preset !== 'custom') {
      const p = SENSITIVITY_PRESETS[preset];
      setHighThreshold(p.high_threshold);
      setMediumThreshold(p.medium_threshold);
    }
  };

  const handleSave = async () => {
    if (mediumThreshold >= highThreshold) {
      toast.error('Medel-tröskel måste vara lägre än Hög-tröskel');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ml_sensitivity: sensitivity,
        ml_high_threshold: highThreshold,
        ml_medium_threshold: mediumThreshold,
      });
      toast.success('ML-inställningar sparade');
    } catch {
      toast.error('Kunde inte spara ML-inställningar');
    } finally {
      setSaving(false);
    }
  };

  const preset = SENSITIVITY_PRESETS[sensitivity] || SENSITIVITY_PRESETS.medium;

  return (
    <div className="mt-3 border border-slate-200 rounded-sm overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors"
        data-testid={`ml-config-toggle-${school.id}`}
      >
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-violet-500" />
          <span className="font-ibm font-semibold text-sm text-slate-700">ML-detektion</span>
          <span className={cn("text-xs font-bold uppercase px-1.5 py-0.5 rounded", preset.bg, preset.color)}>
            {preset.label}
          </span>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="p-4 space-y-4 bg-white" data-testid={`ml-config-panel-${school.id}`}>
          <div>
            <Label className="text-xs font-ibm font-semibold uppercase tracking-wider text-slate-500 mb-2 block">
              Känslighetsnivå
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(SENSITIVITY_PRESETS).map(([key, p]) => (
                <button
                  key={key}
                  onClick={() => handlePresetChange(key)}
                  className={cn(
                    "p-2.5 rounded border text-left transition-all",
                    sensitivity === key
                      ? cn(p.bg, "ring-1 ring-offset-1", key === 'high' && "ring-red-400", key === 'medium' && "ring-orange-400", key === 'low' && "ring-slate-400", key === 'custom' && "ring-violet-400")
                      : "border-slate-200 hover:bg-slate-50"
                  )}
                  data-testid={`ml-preset-${key}-${school.id}`}
                >
                  <p className={cn("font-ibm font-semibold text-sm", sensitivity === key ? p.color : "text-slate-700")}>
                    {p.label}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">{p.description}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3 pt-2 border-t border-slate-100">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-ibm font-semibold text-red-600">
                  Hög tröskel
                </Label>
                <span className="text-xs font-ibm font-bold tabular-nums text-red-600">
                  {(highThreshold * 100).toFixed(0)}%
                </span>
              </div>
              <Slider
                value={[highThreshold * 100]}
                onValueChange={([v]) => {
                  setHighThreshold(v / 100);
                  if (sensitivity !== 'custom') setSensitivity('custom');
                }}
                min={40}
                max={95}
                step={5}
                className="py-1"
                data-testid={`ml-high-slider-${school.id}`}
              />
              <p className="text-[10px] text-slate-400 mt-0.5">
                Poäng över detta = HÖG allvarlighetsgrad
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-ibm font-semibold text-orange-600">
                  Medel troskel
                </Label>
                <span className="text-xs font-ibm font-bold tabular-nums text-orange-600">
                  {(mediumThreshold * 100).toFixed(0)}%
                </span>
              </div>
              <Slider
                value={[mediumThreshold * 100]}
                onValueChange={([v]) => {
                  setMediumThreshold(v / 100);
                  if (sensitivity !== 'custom') setSensitivity('custom');
                }}
                min={15}
                max={75}
                step={5}
                className="py-1"
                data-testid={`ml-medium-slider-${school.id}`}
              />
              <p className="text-[10px] text-slate-400 mt-0.5">
                Poäng över detta = MEDEL allvarlighetsgrad
              </p>
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            size="sm"
            className="w-full bg-violet-600 hover:bg-violet-700"
            data-testid={`ml-save-btn-${school.id}`}
          >
            <Save className="h-3.5 w-3.5 mr-1.5" />
            {saving ? 'Sparar...' : 'Spara ML-inställningar'}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function SchoolsPage() {
  const { isSuperAdmin } = useAuth();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCooldown, setEditingCooldown] = useState(null);
  const [cooldownValue, setCooldownValue] = useState('');

  // Create/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [editingSchool, setEditingSchool] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ name: '', municipality: '', address: '', contact_email: '', contact_phone: '' });

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletingSchool, setDeletingSchool] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Reset-alarms dialog
  const [resetDialog, setResetDialog] = useState(false);
  const [resetSchool, setResetSchool] = useState(null);
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    loadSchools();
  }, []);

  const loadSchools = async () => {
    try {
      const res = await schoolsApi.list();
      setSchools(res.data);
    } catch {
      toast.error('Kunde inte ladda skolor');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setDialogMode('create');
    setFormData({ name: '', municipality: '', address: '', contact_email: '', contact_phone: '' });
    setEditingSchool(null);
    setDialogOpen(true);
  };

  const openEdit = (school) => {
    setDialogMode('edit');
    setFormData({
      name: school.name || '',
      municipality: school.municipality || '',
      address: school.address || '',
      contact_email: school.contact_email || '',
      contact_phone: school.contact_phone || '',
    });
    setEditingSchool(school);
    setDialogOpen(true);
  };

  const handleSaveSchool = async () => {
    if (!formData.name.trim()) {
      toast.error('Skolans namn krävs');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        municipality: formData.municipality.trim() || null,
        address: formData.address.trim() || null,
        contact_email: formData.contact_email.trim() || null,
        contact_phone: formData.contact_phone.trim() || null,
      };
      if (dialogMode === 'create') {
        await schoolsApi.create(payload);
        toast.success('Skola skapad');
      } else {
        await schoolsApi.update(editingSchool.id, payload);
        toast.success('Skola uppdaterad');
      }
      setDialogOpen(false);
      loadSchools();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Något gick fel');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (school) => {
    setDeletingSchool(school);
    setDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deletingSchool) return;
    setDeleting(true);
    try {
      await schoolsApi.delete(deletingSchool.id);
      toast.success('Skola borttagen');
      setDeleteDialog(false);
      setDeletingSchool(null);
      loadSchools();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte ta bort');
    } finally {
      setDeleting(false);
    }
  };

  const confirmReset = (school) => {
    setResetSchool(school);
    setResetConfirm('');
    setResetDialog(true);
  };

  const handleResetAlarms = async () => {
    if (!resetSchool) return;
    setResetting(true);
    try {
      const res = await schoolsApi.resetAlarms(resetSchool.id);
      const d = res.data || {};
      toast.success(`Larm nollställda: ${d.events_deleted || 0} larm och ${d.incidents_deleted || 0} ärenden raderade`);
      setResetDialog(false);
      setResetSchool(null);
      loadSchools();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte nollställa larm');
    } finally {
      setResetting(false);
    }
  };

  const handleUpdateCooldown = async (schoolId) => {
    const minutes = parseInt(cooldownValue);
    if (isNaN(minutes) || minutes < 1 || minutes > 120) {
      toast.error('Cooldown måste vara mellan 1 och 120 minuter');
      return;
    }
    try {
      await schoolsApi.update(schoolId, { cooldown_minutes: minutes });
      toast.success('Cooldown uppdaterad');
      setEditingCooldown(null);
      loadSchools();
    } catch {
      toast.error('Kunde inte uppdatera cooldown');
    }
  };

  const handleSaveMLConfig = async (schoolId, mlConfig) => {
    await schoolsApi.update(schoolId, mlConfig);
    loadSchools();
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-slate-500 font-ibm">Laddar skolor...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="schools-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy tracking-tight">
            Skolor
          </h1>
          <p className="text-slate-500 mt-1 font-ibm">
            Hantera skolor, cooldown-tider och ML-känslighet
          </p>
        </div>
        {isSuperAdmin() && (
          <Button
            onClick={openCreate}
            className="bg-navy hover:bg-navy/90 rounded-sm font-ibm gap-2"
            data-testid="create-school-btn"
          >
            <Plus className="h-4 w-4" />
            Lägg till skola
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {schools.map((school) => (
          <Card key={school.id} className="border-slate-200" data-testid={`school-card-${school.id}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <School className="h-5 w-5 text-navy" />
                  <CardTitle className="font-chivo text-lg">{school.name}</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "text-xs font-ibm font-bold uppercase px-2 py-0.5 rounded",
                    school.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                  )}>
                    {school.is_active ? 'Aktiv' : 'Inaktiv'}
                  </span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(school)} data-testid={`edit-school-${school.id}`}>
                    <Pencil className="h-3.5 w-3.5 text-slate-500" />
                  </Button>
                  {isSuperAdmin() && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-amber-600" title="Nollställ alla larm" onClick={() => confirmReset(school)} data-testid={`reset-alarms-${school.id}`}>
                      <BellOff className="h-3.5 w-3.5 text-slate-500" />
                    </Button>
                  )}
                  {isSuperAdmin() && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-red-600" onClick={() => confirmDelete(school)} data-testid={`delete-school-${school.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {school.address && (
                <p className="text-sm text-slate-600">{school.address}</p>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 p-2 rounded">
                  <p className="text-xs text-slate-500 font-ibm uppercase tracking-wider">Rum</p>
                  <p className="font-semibold text-navy">{school.room_count || 0}</p>
                </div>
                <div className="bg-slate-50 p-2 rounded">
                  <p className="text-xs text-slate-500 font-ibm uppercase tracking-wider">Enheter</p>
                  <p className="font-semibold text-navy">{school.device_count || 0}</p>
                </div>
              </div>

              {/* Cooldown Section */}
              <div className="flex items-center justify-between py-2 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-600 font-ibm">Larm-cooldown</span>
                </div>
                {editingCooldown === school.id ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={cooldownValue}
                      onChange={(e) => setCooldownValue(e.target.value)}
                      className="w-20 h-7 text-sm"
                      min={1}
                      max={120}
                      data-testid={`cooldown-input-${school.id}`}
                    />
                    <span className="text-xs text-slate-500">min</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs"
                      onClick={() => handleUpdateCooldown(school.id)}
                      data-testid={`cooldown-save-${school.id}`}
                    >
                      Spara
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs text-slate-400"
                      onClick={() => setEditingCooldown(null)}
                    >
                      Avbryt
                    </Button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setEditingCooldown(school.id); setCooldownValue(String(school.cooldown_minutes || 10)); }}
                    className="text-sm font-ibm font-semibold text-navy hover:underline"
                    data-testid={`cooldown-edit-${school.id}`}
                  >
                    {school.cooldown_minutes || 10} min
                  </button>
                )}
              </div>

              {/* ML Config Panel */}
              <MLConfigPanel
                school={school}
                onSave={(config) => handleSaveMLConfig(school.id, config)}
              />
            </CardContent>
          </Card>
        ))}

        {schools.length === 0 && (
          <Card className="border-slate-200 md:col-span-2">
            <CardContent className="py-12 text-center">
              <School className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-chivo text-lg text-slate-700">Inga skolor registrerade</h3>
              <p className="text-slate-500 mt-1 font-ibm">
                Lägg till en skola för att komma igång
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit School Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="school-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo">
              {dialogMode === 'create' ? 'Lägg till skola' : 'Redigera skola'}
            </DialogTitle>
            <DialogDescription className="font-ibm">
              {dialogMode === 'create' ? 'Skapa en ny skola i systemet' : 'Uppdatera skolans uppgifter'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="font-ibm text-xs uppercase tracking-widest">Skolans namn *</Label>
              <Input
                value={formData.name}
                onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                placeholder="Strandskolan"
                data-testid="school-form-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm text-xs uppercase tracking-widest">Kommun / Kund</Label>
              <Input
                value={formData.municipality}
                onChange={e => setFormData(f => ({ ...f, municipality: e.target.value }))}
                placeholder="Kils kommun"
                data-testid="school-form-municipality"
              />
              <p className="text-[11px] text-slate-400 font-ibm">Visas på förpackningsetiketter. Lämnas tomt → organisationsnamn används.</p>
            </div>
            <div className="space-y-2">
              <Label className="font-ibm text-xs uppercase tracking-widest">Adress</Label>
              <Input
                value={formData.address}
                onChange={e => setFormData(f => ({ ...f, address: e.target.value }))}
                placeholder="Skolvägen 1, 123 45 Stockholm"
                data-testid="school-form-address"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-ibm text-xs uppercase tracking-widest">Kontakt e-post</Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={e => setFormData(f => ({ ...f, contact_email: e.target.value }))}
                  placeholder="rektor@skola.se"
                  data-testid="school-form-email"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-ibm text-xs uppercase tracking-widest">Telefon</Label>
                <Input
                  value={formData.contact_phone}
                  onChange={e => setFormData(f => ({ ...f, contact_phone: e.target.value }))}
                  placeholder="08-123 456"
                  data-testid="school-form-phone"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="font-ibm">Avbryt</Button>
            <Button onClick={handleSaveSchool} disabled={saving} className="bg-navy hover:bg-navy/90 font-ibm gap-2" data-testid="school-form-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {dialogMode === 'create' ? 'Skapa' : 'Spara'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete School Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-school-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo">Ta bort skola</DialogTitle>
            <DialogDescription className="font-ibm">
              Är du säker på att du vill ta bort <strong>{deletingSchool?.name}</strong>?
              Alla rum och enheter kopplade till skolan tas också bort.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)} className="font-ibm">Avbryt</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} className="font-ibm gap-2" data-testid="confirm-delete-school">
              {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
              Ta bort
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Alarms Dialog */}
      <Dialog open={resetDialog} onOpenChange={setResetDialog}>
        <DialogContent className="sm:max-w-md" data-testid="reset-alarms-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" /> Nollställ alla larm
            </DialogTitle>
            <DialogDescription className="font-ibm">
              Detta raderar <strong>permanent</strong> alla larm (händelser) och ärenden för
              <strong> {resetSchool?.name}</strong>, även ur Larmhistorik. Enheternas larmstatus nollställs.
              Åtgärden går <strong>inte</strong> att ångra.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <Label className="font-ibm text-xs uppercase tracking-widest text-slate-500">
              Skriv <span className="font-bold text-amber-700">NOLLSTÄLL</span> för att bekräfta
            </Label>
            <Input
              value={resetConfirm}
              onChange={e => setResetConfirm(e.target.value)}
              placeholder="NOLLSTÄLL"
              data-testid="reset-alarms-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialog(false)} className="font-ibm">Avbryt</Button>
            <Button
              variant="destructive"
              onClick={handleResetAlarms}
              disabled={resetting || resetConfirm.trim().toUpperCase() !== 'NOLLSTÄLL'}
              className="font-ibm gap-2 bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-reset-alarms"
            >
              {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellOff className="h-4 w-4" />}
              Nollställ larm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
